import React, {useState, useMemo} from 'react';
import {Box, Typography, Card, CardContent} from '@mui/material';
import ArrTreeItemSelector, {
  TabItem,
  SelectedItemsData,
  GroupedItemsByTab,
} from '../../../components/common/arr-tree-item-selector/ArrTreeItemSelector';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';

// Helper function to add treeId to all tree items recursively
const addTreeIdToItems = (
  items: TreeViewBaseItem<ExtendedTreeItemProps>[],
  treeId: string
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  return items.map((item) => ({
    ...item,
    treeId,
    children: item.children
      ? addTreeIdToItems(item.children, treeId)
      : undefined,
  }));
};

const TreeItemSelectorDemo: React.FC = () => {
  const tabs: TabItem[] = [
    {
      tabTitle: 'Metadata',
      groups: [
        {
          groupTitle: 'Document Fields',
          prefix: 'Tiff-Metadata:',
          preselectedItemIdField: 'id',
          treeItemIdField: 'id',
          treeId: 'metadata-document-fields',
          treeData: [
            {
              id: 'field1-1',
              label: 'AppointmentEndDateOnly',
              name: 'appointment_end_date_only',
            },
            {
              id: 'field1-2',
              label: 'AppointmentStartDateOnly',
              name: 'appointment_start_date_only',
            },
            {
              id: 'field1-3',
              label: 'AttachDocIDs',
              name: 'attach_doc_ids',
            },
            {
              id: 'field1-4',
              label: 'Container',
              name: 'container',
            },
            {
              id: 'field1-5',
              label: 'Document Title',
              name: 'document_title',
            },
            {
              id: 'field1-6',
              label: 'Author',
              name: 'author',
            },
            {
              id: 'field1-7',
              label: 'Date Created',
              name: 'date_created',
            },
            {
              id: 'field1-8',
              label: 'Date Modified',
              name: 'date_modified',
            },
            {
              id: 'field1-9',
              label: 'File Extension',
              name: 'file_extension',
            },
            {
              id: 'field1-10',
              label: 'File Size',
              name: 'file_size',
            },
            {
              id: 'field1-11',
              label: 'Document Type',
              name: 'document_type',
            },
            {
              id: 'field1-12',
              label: 'Source Path',
              name: 'source_path',
            },
          ],
          preSelectedItems: [
            {id: 'field1-1', label: 'AppointmentEndDateOnly'},
            {id: 'field1-2', label: 'AppointmentStartDateOnly'},
            {id: 'field1-3', label: 'AttachDocIDs'},
            {id: 'field1-4', label: 'Container'},
          ],
        },
        {
          groupTitle: 'Status Fields',
          prefix: 'Tiff-Metadata:',
          preselectedItemIdField: 'id',
          treeItemIdField: 'id',
          treeId: 'metadata-status-fields',
          treeData: [
            {
              id: 'field2-1',
              label: 'Status',
              name: 'status',
              children: [
                {
                  id: 'field2-1-1',
                  label: 'Active Status',
                  name: 'active_status',
                },
                {
                  id: 'field2-1-2',
                  label: 'Review Status',
                  name: 'review_status',
                },
                {
                  id: 'field2-1-3',
                  label: 'Approved Status',
                  name: 'approved_status',
                },
              ],
            },
            {
              id: 'field2-2',
              label: 'Priority',
              name: 'priority',
            },
            {
              id: 'field2-3',
              label: 'Confidentiality Level',
              name: 'confidentiality_level',
            },
            {
              id: 'field2-4',
              label: 'Reviewer',
              name: 'reviewer',
            },
            {
              id: 'field2-5',
              label: 'Assignee',
              name: 'assignee',
            },
            {
              id: 'field2-6',
              label: 'Completion Date',
              name: 'completion_date',
            },
            {
              id: 'field2-7',
              label: 'Due Date',
              name: 'due_date',
            },
            {
              id: 'field2-8',
              label: 'Status Code',
              name: 'status_code',
            },
            {
              id: 'field2-9',
              label: 'Workflow Stage',
              name: 'workflow_stage',
            },
            {
              id: 'field2-10',
              label: 'Processing Status',
              name: 'processing_status',
            },
          ],
          preSelectedItems: [{id: 'field2-1-1', label: 'Active Status'}],
        },
      ],
    },
    {
      tabTitle: 'Work Product',
      groups: [
        {
          groupTitle: 'Folders',
          prefix: 'Tiff-Folders:',
          preselectedItemIdField: 'id',
          treeItemIdField: 'id',
          treeId: 'workproduct-folders',
          treeData: [
            {
              id: 'all-folders',
              label: 'All Folders',
              name: 'all_folders',
              children: [
                {
                  id: 'folder1',
                  label: 'Productions',
                  name: 'productions',
                },
                {
                  id: 'folder2',
                  label: 'Datasets',
                  name: 'datasets',
                },
                {
                  id: 'folder3',
                  label: 'Exhibits',
                  name: 'exhibits',
                },
                {
                  id: 'folder4',
                  label: 'Transcripts',
                  name: 'transcripts',
                },
                {
                  id: 'folder5',
                  label: 'Depositions',
                  name: 'depositions',
                },
                {
                  id: 'folder6',
                  label: 'Correspondence',
                  name: 'correspondence',
                },
                {
                  id: 'folder7',
                  label: 'Contracts',
                  name: 'contracts',
                },
                {
                  id: 'folder8',
                  label: 'Financial Records',
                  name: 'financial_records',
                },
                {
                  id: 'folder9',
                  label: 'Email Archives',
                  name: 'email_archives',
                },
                {
                  id: 'folder10',
                  label: 'Legal Briefs',
                  name: 'legal_briefs',
                },
                {
                  id: 'folder11',
                  label: 'Case Files',
                  name: 'case_files',
                },
                {
                  id: 'folder12',
                  label: 'Evidence',
                  name: 'evidence',
                },
              ],
            },
          ],
        },
        {
          groupTitle: 'Tags',
          prefix: 'TiffTags:',
          preselectedItemIdField: 'id',
          treeItemIdField: 'id',
          treeId: 'workproduct-tags',
          treeData: [
            {
              id: 'all-tags',
              label: 'All Tags',
              name: 'all_tags',
              children: [
                {
                  id: 'tag1',
                  label: 'alldoc',
                  name: 'alldoc',
                },
                {
                  id: 'tag2',
                  label: 'Attorney_Client',
                  name: 'attorney_client',
                },
                {
                  id: 'tag3',
                  label: 'Attorney_WorkProduct',
                  name: 'attorney_workproduct',
                },
                {
                  id: 'tag4',
                  label: 'Confidential',
                  name: 'confidential',
                },
                {
                  id: 'tag5',
                  label: 'Foreign_Language',
                  name: 'foreign_language',
                },
                {
                  id: 'tag6',
                  label: 'Hidden_Content',
                  name: 'hidden_content',
                },
                {
                  id: 'tag7',
                  label: 'Privileged',
                  name: 'privileged',
                },
                {
                  id: 'tag8',
                  label: 'Redacted',
                  name: 'redacted',
                },
                {
                  id: 'tag9',
                  label: 'Reviewed',
                  name: 'reviewed',
                },
                {
                  id: 'tag10',
                  label: 'Responsive',
                  name: 'responsive',
                },
                {
                  id: 'tag11',
                  label: 'Non-Responsive',
                  name: 'non_responsive',
                },
                {
                  id: 'tag12',
                  label: 'Hot Document',
                  name: 'hot_document',
                },
                {
                  id: 'tag13',
                  label: 'Important',
                  name: 'important',
                },
                {
                  id: 'tag14',
                  label: 'Follow Up',
                  name: 'follow_up',
                },
                {
                  id: 'tag15',
                  label: 'Archive',
                  name: 'archive',
                },
              ],
            },
          ],
          preSelectedItems: [
            {id: 'tag1', label: 'alldoc'},
            {id: 'tag2', label: 'Attorney_Client'},
            {id: 'tag3', label: 'Attorney_WorkProduct'},
            {id: 'tag4', label: 'Confidential'},
            {id: 'tag5', label: 'Foreign_Language'},
            {id: 'tag6', label: 'Hidden_Content'},
          ],
        },
      ],
    },
    {
      tabTitle: 'Calculated',
      groups: [
        {
          groupTitle: 'Calculated Fields',
          prefix: 'Tiff-Calculated:',
          preselectedItemIdField: 'id',
          treeItemIdField: 'id',
          treeId: 'calculated-fields',
          treeData: [
            {
              id: 'calc1',
              label: 'MP3Path',
              name: 'mp3_path',
            },
            {
              id: 'calc2',
              label: 'NativePath',
              name: 'native_path',
            },
            {
              id: 'calc3',
              label: 'Total Pages',
              name: 'total_pages',
            },
            {
              id: 'calc4',
              label: 'Word Count',
              name: 'word_count',
            },
            {
              id: 'calc5',
              label: 'Review Score',
              name: 'review_score',
            },
            {
              id: 'calc6',
              label: 'Page Count',
              name: 'page_count',
            },
            {
              id: 'calc7',
              label: 'Character Count',
              name: 'character_count',
            },
            {
              id: 'calc8',
              label: 'Line Count',
              name: 'line_count',
            },
            {
              id: 'calc9',
              label: 'Paragraph Count',
              name: 'paragraph_count',
            },
            {
              id: 'calc10',
              label: 'Sentence Count',
              name: 'sentence_count',
            },
            {
              id: 'calc11',
              label: 'Reading Time',
              name: 'reading_time',
            },
            {
              id: 'calc12',
              label: 'Complexity Score',
              name: 'complexity_score',
            },
            {
              id: 'calc13',
              label: 'Relevance Score',
              name: 'relevance_score',
            },
            {
              id: 'calc14',
              label: 'File Hash',
              name: 'file_hash',
            },
            {
              id: 'calc15',
              label: 'Duplicate Count',
              name: 'duplicate_count',
            },
          ],
          preSelectedItems: [
            {id: 'calc1', label: 'MP3Path'},
            {id: 'calc2', label: 'NativePath'},
          ],
        },
      ],
    },
  ];

  // Process tabs to add treeId to all tree items
  const processedTabs = useMemo(() => {
    return tabs.map((tab) => ({
      ...tab,
      groups: tab.groups.map((group) => ({
        ...group,
        treeData: group.treeId
          ? addTreeIdToItems(group.treeData, group.treeId)
          : group.treeData,
      })),
    }));
  }, []);

  const tabSingle: TabItem[] = [
    {
      tabTitle: 'Work Product',
      groups: [
        {
          groupTitle: 'Folders',
          prefix: 'Tiff-Folders:',
          preselectedItemIdField: 'id',
          treeItemIdField: 'id',
          treeId: 'single-tab-folders',
          treeData: [
            {
              id: 'root-category',
              label: 'Work Product Categories',
              name: 'work_product_categories',
              children: [
                {
                  id: 'cat-documents',
                  label: 'Documents',
                  name: 'documents',
                  children: [
                    {
                      id: 'doc-productions',
                      label: 'Productions',
                      name: 'productions',
                    },
                    {
                      id: 'doc-datasets',
                      label: 'Datasets',
                      name: 'datasets',
                      children: [
                        {
                          id: 'doc-exhibits',
                          label: 'Exhibits',
                          name: 'exhibits',
                        },
                        {
                          id: 'doc-contracts',
                          label: 'Contracts',
                          name: 'contracts',
                        },
                      ],
                    },
                  ],
                },
                {
                  id: 'cat-legal',
                  label: 'Legal Materials',
                  name: 'legal_materials',
                  children: [
                    {
                      id: 'legal-briefs',
                      label: 'Legal Briefs',
                      name: 'legal_briefs',
                    },
                    {
                      id: 'legal-case-files',
                      label: 'Case Files',
                      name: 'case_files',
                    },
                    {
                      id: 'legal-evidence',
                      label: 'Evidence',
                      name: 'evidence',
                    },
                    {
                      id: 'legal-depositions',
                      label: 'Depositions',
                      name: 'depositions',
                    },
                  ],
                },
                {
                  id: 'cat-communications',
                  label: 'Communications',
                  name: 'communications',
                  children: [
                    {
                      id: 'comm-email',
                      label: 'Email Archives',
                      name: 'email_archives',
                    },
                    {
                      id: 'comm-correspondence',
                      label: 'Correspondence',
                      name: 'correspondence',
                    },
                    {
                      id: 'comm-transcripts',
                      label: 'Transcripts',
                      name: 'transcripts',
                    },
                  ],
                },
                {
                  id: 'cat-financial',
                  label: 'Financial Records',
                  name: 'financial_records',
                  children: [
                    {
                      id: 'fin-statements',
                      label: 'Bank Statements',
                      name: 'bank_statements',
                    },
                    {
                      id: 'fin-invoices',
                      label: 'Invoices',
                      name: 'invoices',
                    },
                    {
                      id: 'fin-receipts',
                      label: 'Receipts',
                      name: 'receipts',
                    },
                  ],
                },
              ],
            },
          ],
          preSelectedItems: [
            {id: 'doc-productions', label: 'Productions'},
            {id: 'legal-briefs', label: 'Legal Briefs'},
          ],
        },
      ],
    },
  ];

  // Process tabSingle to add treeId to all tree items
  const processedTabSingle = useMemo(() => {
    return tabSingle.map((tab) => ({
      ...tab,
      groups: tab.groups.map((group) => ({
        ...group,
        treeData: group.treeId
          ? addTreeIdToItems(group.treeData, group.treeId)
          : group.treeData,
      })),
    }));
  }, []);

  const [selectedItems, setSelectedItems] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);

  const [groupedByTab, setGroupedByTab] = useState<GroupedItemsByTab[]>([]);

  const [selectedItemsSingle, setSelectedItemsSingle] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);

  const [groupedByTabSingle, setGroupedByTabSingle] = useState<
    GroupedItemsByTab[]
  >([]);

  const handleItemsChange = (data: SelectedItemsData) => {
    setSelectedItems(data.items);
    if (data.groupedByTab) {
      setGroupedByTab(data.groupedByTab);
    }
  };

  const handleItemsChangeSingle = (data: SelectedItemsData) => {
    setSelectedItemsSingle(data.items);
    if (data.groupedByTab) {
      setGroupedByTabSingle(data.groupedByTab);
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          Tree Item Transfer and Drag & Drop Demo
        </Typography>
      </Box>

      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h5"
          className="typo-text headline2_size"
          gutterBottom
        >
          Single Tab Example
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '5rem',
        }}
      >
        <Box>
          <Box
            sx={{
              background: '#182231',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <ArrTreeItemSelector
              tabs={processedTabSingle}
              onSelectedItemsChange={handleItemsChangeSingle}
              isPreExpanded={true}
            />
          </Box>
        </Box>

        <Box sx={{display: 'flex', flexDirection: 'row', gap: 2}}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead2_size"
                gutterBottom
              >
                Selected Items ({selectedItemsSingle.length})
              </Typography>
              {selectedItemsSingle.length === 0 ? (
                <Typography
                  className="typo-text body3_size"
                  sx={{color: '#666'}}
                >
                  No items selected yet
                </Typography>
              ) : (
                <Box
                  sx={{
                    backgroundColor: '#f9f9f9',
                    padding: 2,
                    borderRadius: 1,
                    maxHeight: '200px',
                    overflow: 'auto',
                    textAlign: 'left',
                  }}
                >
                  <pre style={{margin: 0, fontSize: '12px'}}>
                    {JSON.stringify(selectedItemsSingle, null, 2)}
                  </pre>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead2_size"
                gutterBottom
              >
                Grouped by Tab
              </Typography>
              {groupedByTabSingle.length === 0 ? (
                <Typography
                  className="typo-text body3_size"
                  sx={{color: '#666'}}
                >
                  No items grouped by tab yet
                </Typography>
              ) : (
                <Box
                  sx={{
                    backgroundColor: '#f9f9f9',
                    padding: 2,
                    borderRadius: 1,
                    maxHeight: '300px',
                    overflow: 'auto',
                    textAlign: 'left',
                  }}
                >
                  {groupedByTabSingle.map((tabGroup, tabIndex) => (
                    <Box key={tabIndex} sx={{marginBottom: 2}}>
                      <Typography
                        variant="subtitle2"
                        className="typo-text subhead4_size"
                        sx={{fontWeight: 'bold', marginBottom: 1}}
                      >
                        Tab {tabGroup.tabIndex}: {tabGroup.tabTitle}
                      </Typography>
                      {tabGroup.groups.map((group, groupIndex) => (
                        <Box
                          key={groupIndex}
                          sx={{
                            backgroundColor: '#ffffff',
                            padding: 1,
                            borderRadius: 1,
                            marginLeft: 2,
                            marginBottom: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            className="typo-text body3_size"
                            sx={{fontWeight: 'bold', marginBottom: 0.5}}
                          >
                            {group.groupTitle} ({group.items.length} items)
                          </Typography>
                          <pre style={{margin: 0, fontSize: '10px'}}>
                            {JSON.stringify(group.items, null, 2)}
                          </pre>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{marginTop: 6, marginBottom: 4}}>
        <Typography
          variant="h5"
          className="typo-text headline2_size"
          gutterBottom
        >
          Multiple Tabs Example
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '5rem',
        }}
      >
        <Box>
          <Box
            sx={{
              background: '#182231',
              borderRadius: '8px',
              padding: '12px',
            }}
          >
            <ArrTreeItemSelector
              tabs={processedTabs}
              onSelectedItemsChange={handleItemsChange}
              isPreExpanded={true}
            />
          </Box>
        </Box>

        <Box sx={{display: 'flex', flexDirection: 'row', gap: 2}}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead2_size"
                gutterBottom
              >
                Selected Items ({selectedItems.length})
              </Typography>
              {selectedItems.length === 0 ? (
                <Typography
                  className="typo-text body3_size"
                  sx={{color: '#666'}}
                >
                  No items selected yet
                </Typography>
              ) : (
                <Box
                  sx={{
                    backgroundColor: '#f9f9f9',
                    padding: 2,
                    borderRadius: 1,
                    maxHeight: '200px',
                    overflow: 'auto',
                    textAlign: 'left',
                  }}
                >
                  <pre style={{margin: 0, fontSize: '12px'}}>
                    {JSON.stringify(selectedItems, null, 2)}
                  </pre>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead2_size"
                gutterBottom
              >
                Grouped by Tab
              </Typography>
              {groupedByTab.length === 0 ? (
                <Typography
                  className="typo-text body3_size"
                  sx={{color: '#666'}}
                >
                  No items grouped by tab yet
                </Typography>
              ) : (
                <Box
                  sx={{
                    backgroundColor: '#f9f9f9',
                    padding: 2,
                    borderRadius: 1,
                    maxHeight: '300px',
                    overflow: 'auto',
                    textAlign: 'left',
                  }}
                >
                  {groupedByTab.map((tabGroup, tabIndex) => (
                    <Box key={tabIndex} sx={{marginBottom: 2}}>
                      <Typography
                        variant="subtitle2"
                        className="typo-text subhead4_size"
                        sx={{fontWeight: 'bold', marginBottom: 1}}
                      >
                        Tab {tabGroup.tabIndex}: {tabGroup.tabTitle}
                      </Typography>
                      {tabGroup.groups.map((group, groupIndex) => (
                        <Box
                          key={groupIndex}
                          sx={{
                            backgroundColor: '#ffffff',
                            padding: 1,
                            borderRadius: 1,
                            marginLeft: 2,
                            marginBottom: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            className="typo-text body3_size"
                            sx={{fontWeight: 'bold', marginBottom: 0.5}}
                          >
                            {group.groupTitle} ({group.items.length} items)
                          </Typography>
                          <pre style={{margin: 0, fontSize: '10px'}}>
                            {JSON.stringify(group.items, null, 2)}
                          </pre>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default TreeItemSelectorDemo;
